import type { CSSProperties } from 'react';

/**
 * React Native style object -> CSS.
 *
 * WHY THIS EXISTS
 * ---------------
 * The web app has to be visually identical to the React Native app, and the RN
 * screens express their design as plain style objects (`createStyles(C)`).
 * Re-deriving those numbers by hand into hand-written CSS is where a "port"
 * silently drifts: a 13px radius becomes 12, a 14px gap becomes 16, and after
 * seventy screens the two apps no longer look like the same product.
 *
 * So the design values cross over verbatim and this module — one file, no
 * dependencies — resolves the handful of places where RN's style dialect and
 * CSS disagree. Everything it emits is ordinary CSS on ordinary DOM elements;
 * nothing here ships a virtual layout engine.
 *
 * WHAT IT ACTUALLY CHANGES
 * ------------------------
 *  - `paddingHorizontal` / `marginVertical` / `paddingStart` have no CSS
 *    equivalent; they become the logical shorthands (`padding-inline`, ...).
 *  - Physical horizontal edges (`marginLeft`, `right`, `textAlign: 'left'`)
 *    also become logical, so Arabic mirrors the way `I18nManager.forceRTL`
 *    mirrors the native app.
 *  - `borderWidth` alone paints nothing in CSS — a border style is added, but
 *    only for the EDGES that declared a width (see BORDER_STYLE_FOR).
 *  - `lineHeight: 20` means 20px in RN and 20x in CSS. Converted.
 *  - `shadow*` / `elevation` collapse into one `box-shadow`.
 *  - `transform: [{ translateY: 2 }]` becomes `translateY(2px)`.
 *  - `fontVariant: ['tabular-nums']` becomes `font-variant-numeric`.
 *  - `fontFamily: 'Marcellus'` gains the web fallback stack it needs.
 *  - Bare numbers get `px`, except for the genuinely unitless properties.
 */

export type Style = Record<string, any>;
/** Generic like RN's, so `StyleProp<ViewStyle>` in a ported file still checks. */
export type StyleProp<T = Style> = T | false | null | undefined | ReadonlyArray<StyleProp<T>>;

/** CSS properties that take a bare number (React's own unitless list, trimmed). */
const UNITLESS = new Set([
  'opacity',
  'zIndex',
  'flex',
  'flexGrow',
  'flexShrink',
  'order',
  'aspectRatio',
  'fontWeight',
  'WebkitLineClamp',
  'columnCount',
  'zoom',
]);

/** RN box props with no CSS name — mapped to the logical shorthands. */
const EDGE_MAP: Record<string, string> = {
  paddingHorizontal: 'paddingInline',
  paddingVertical: 'paddingBlock',
  marginHorizontal: 'marginInline',
  marginVertical: 'marginBlock',
  paddingStart: 'paddingInlineStart',
  paddingEnd: 'paddingInlineEnd',
  marginStart: 'marginInlineStart',
  marginEnd: 'marginInlineEnd',
  paddingLeft: 'paddingInlineStart',
  paddingRight: 'paddingInlineEnd',
  marginLeft: 'marginInlineStart',
  marginRight: 'marginInlineEnd',
  borderStartWidth: 'borderInlineStartWidth',
  borderEndWidth: 'borderInlineEndWidth',
  borderLeftWidth: 'borderInlineStartWidth',
  borderRightWidth: 'borderInlineEndWidth',
  borderStartColor: 'borderInlineStartColor',
  borderEndColor: 'borderInlineEndColor',
  borderLeftColor: 'borderInlineStartColor',
  borderRightColor: 'borderInlineEndColor',
  borderTopStartRadius: 'borderStartStartRadius',
  borderTopEndRadius: 'borderStartEndRadius',
  borderBottomStartRadius: 'borderEndStartRadius',
  borderBottomEndRadius: 'borderEndEndRadius',
  borderTopLeftRadius: 'borderStartStartRadius',
  borderTopRightRadius: 'borderStartEndRadius',
  borderBottomLeftRadius: 'borderEndStartRadius',
  borderBottomRightRadius: 'borderEndEndRadius',
  start: 'insetInlineStart',
  end: 'insetInlineEnd',
  left: 'insetInlineStart',
  right: 'insetInlineEnd',
  resizeMode: 'objectFit',
};

/**
 * A width means "there is a border on this edge", and CSS will not paint one
 * without a style. The style has to be set PER EDGE, not with the `border-style`
 * shorthand: that turns all four edges on, and the three RN never mentioned then
 * take their CSS initial values — `medium` (3px) of `currentColor`, which on
 * this app's surfaces is a 3px near-white frame. A bottom bar with a hairline
 * top border came out boxed in white.
 *
 * Order matters: the shorthand is first so a per-edge width can still override
 * it, since React writes these onto the node in insertion order.
 */
const BORDER_STYLE_FOR: Record<string, string> = {
  borderWidth: 'borderStyle',
  borderTopWidth: 'borderTopStyle',
  borderBottomWidth: 'borderBottomStyle',
  borderInlineStartWidth: 'borderInlineStartStyle',
  borderInlineEndWidth: 'borderInlineEndStyle',
};

/**
 * A bare family name is all RN needs — the face is bundled with the binary. The
 * web has to name a fallback for the seconds before the webfont arrives, and for
 * the sessions where it never does.
 */
const FONT_STACKS: Record<string, string> = {
  Marcellus: "'Marcellus', 'Times New Roman', Georgia, serif",
  Inter: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  'Noto Kufi Arabic': "'Noto Kufi Arabic', 'Inter', system-ui, sans-serif",
};

/** RN transform key -> the unit CSS needs for it. */
const TRANSFORM_UNITS: Record<string, string> = {
  translateX: 'px',
  translateY: 'px',
  perspective: 'px',
  rotate: '',
  rotateX: '',
  rotateY: '',
  rotateZ: '',
  skewX: '',
  skewY: '',
  scale: '',
  scaleX: '',
  scaleY: '',
};

/** Re-express `color` at `alpha`. Handles #rgb, #rrggbb and rgb()/rgba(). */
export function withAlpha(color: string, alpha: number): string {
  if (alpha >= 1) return color;
  const value = color.trim();
  if (value.startsWith('#')) {
    const body = value.slice(1);
    const full =
      body.length === 3 || body.length === 4
        ? body
            .slice(0, 3)
            .split('')
            .map((c) => c + c)
            .join('')
        : body.slice(0, 6);
    const n = parseInt(full, 16);
    if (Number.isNaN(n)) return value;
    return 'rgba(' + ((n >> 16) & 255) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255) + ', ' + alpha + ')';
  }
  const m = value.match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(',').map((p) => p.trim());
    const a = parts[3] !== undefined ? Number(parts[3]) * alpha : alpha;
    return 'rgba(' + parts[0] + ', ' + parts[1] + ', ' + parts[2] + ', ' + a + ')';
  }
  return value; // named colour — CSS has no alpha syntax for it, keep it opaque
}

function px(value: unknown): unknown {
  return typeof value === 'number' ? value + 'px' : value;
}

function transformToCSS(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return undefined;
  const parts: string[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    for (const [fn, raw] of Object.entries(entry as Record<string, unknown>)) {
      const unit = TRANSFORM_UNITS[fn] ?? '';
      parts.push(fn + '(' + (typeof raw === 'number' ? raw + unit : raw) + ')');
    }
  }
  return parts.length ? parts.join(' ') : undefined;
}

/** Collapse RN's five shadow props (or Android's `elevation`) into one box-shadow. */
function shadowToCSS(s: Style): string | undefined {
  const hasShadow =
    s.shadowColor !== undefined || s.shadowRadius !== undefined || s.shadowOpacity !== undefined;
  if (hasShadow) {
    const offset = (s.shadowOffset ?? {}) as { width?: number; height?: number };
    const x = offset.width ?? 0;
    const y = offset.height ?? 0;
    const blur = s.shadowRadius ?? 0;
    const opacity = s.shadowOpacity ?? 1;
    const color = withAlpha(String(s.shadowColor ?? '#000000'), opacity);
    return x + 'px ' + y + 'px ' + blur + 'px ' + color;
  }
  if (typeof s.elevation === 'number' && s.elevation > 0) {
    const e = s.elevation;
    return '0px ' + Math.round(e * 0.8) + 'px ' + e * 2 + 'px rgba(4, 30, 20, 0.34)';
  }
  return undefined;
}

const SHADOW_KEYS = new Set([
  'shadowColor',
  'shadowOffset',
  'shadowOpacity',
  'shadowRadius',
  'elevation',
]);

/** No CSS meaning — dropped rather than emitted as an invalid declaration. */
const DROP_KEYS = new Set([
  'includeFontPadding',
  'textAlignVertical',
  'writingDirection',
  'tintColor',
]);

/** Flatten nested/conditional style props (`[a, cond && b, c]`) into one object. */
export function flatten(...styles: StyleProp<Style>[]): Style {
  const out: Style = {};
  const walk = (s: StyleProp<Style>) => {
    if (!s) return;
    if (Array.isArray(s)) {
      for (const item of s) walk(item);
      return;
    }
    Object.assign(out, s);
  };
  for (const s of styles) walk(s);
  return out;
}

/** Flatten, then translate to a React `style` object. */
export function sx(...styles: StyleProp<Style>[]): CSSProperties {
  const flat = flatten(...styles);
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(flat)) {
    if (value === undefined || value === null) continue;
    if (SHADOW_KEYS.has(key)) continue; // handled after the loop
    if (DROP_KEYS.has(key)) continue;

    if (key === 'transform') {
      const t = transformToCSS(value);
      if (t) out.transform = t;
      continue;
    }
    if (key === 'lineHeight') {
      out.lineHeight = px(value);
      continue;
    }
    if (key === 'fontFamily') {
      out.fontFamily = FONT_STACKS[String(value)] ?? value;
      continue;
    }
    if (key === 'fontVariant') {
      // RN's array of feature names; CSS splits these across two properties and
      // the only one this app uses is the numeric family (tabular figures for
      // scores, prices, timers).
      const features = Array.isArray(value) ? value : [value];
      out.fontVariantNumeric = features.join(' ');
      continue;
    }
    if (key === 'textAlign') {
      out.textAlign = value === 'left' ? 'start' : value === 'right' ? 'end' : value;
      continue;
    }

    const cssKey = EDGE_MAP[key] ?? key;
    out[cssKey] = UNITLESS.has(cssKey) ? value : px(value);
  }

  // RN paints a border only where a width says so, whatever borderStyle says,
  // so an author's style is honoured on those edges and dropped everywhere else.
  const borderStyle = typeof out.borderStyle === 'string' ? out.borderStyle : 'solid';
  delete out.borderStyle;
  for (const [widthKey, styleKey] of Object.entries(BORDER_STYLE_FOR)) {
    if (out[widthKey] !== undefined) out[styleKey] = borderStyle;
  }

  const shadow = shadowToCSS(flat);
  if (shadow) out.boxShadow = shadow;

  return out as CSSProperties;
}

/**
 * Stands in for RN's `StyleSheet` so the ported `createStyles(C)` blocks need no
 * edits. `create` is an identity function with types attached — RN's
 * registry-of-ids optimisation is a native-bridge concern that does not exist
 * here — and `hairlineWidth` is the thinnest line the display can draw.
 */
export const StyleSheet = {
  create<T extends Record<string, Style>>(styles: T): T {
    return styles;
  },
  flatten,
  hairlineWidth: typeof window !== 'undefined' ? 1 / (window.devicePixelRatio || 1) : 0.5,
  absoluteFill: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as Style,
  absoluteFillObject: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as Style,
};

export type ViewStyle = Style;
export type TextStyle = Style;
export type ImageStyle = Style;
